import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SUBSCRIPTION_PLANS,
  formatEuro,
  type SubscriptionPlanKey,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n } from '@/features/i18n/I18nProvider';
import { useAccessState } from '@/features/subscription/RequireFeatureAccess';
import {
  daysUntilDate,
  formatSubscriptionDate,
  getSubscriptionAlert,
  planLabel,
} from '@/features/subscription/access';
import { useToast } from '@/features/ui/toast/useToast';

type SubRow = {
  id: string;
  plan_type: string;
  price: number | null;
  currency: string | null;
  starts_on: string | null;
  ends_on: string | null;
  status: string;
  installment_count: number;
  installments_paid: number;
  next_installment_at: string | null;
};

async function invokeGeniusPay(body: Record<string, unknown>) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Session expirée');

  const { data, error } = await supabase.functions.invoke('genius-pay', {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data as { checkout_url: string };
}

export function SubscriptionPage() {
  const { profile } = useAuth();
  const { t, locale } = useI18n();
  const { state } = useAccessState();
  const toast = useToast();
  const [params] = useSearchParams();
  const [current, setCurrent] = useState<SubRow | null>(null);
  const [history, setHistory] = useState<SubRow[]>([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!profile) return;
    const { data, error: err } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });
    if (err) {
      toast.error(err.message);
      return;
    }
    const rows = (data ?? []) as SubRow[];
    setHistory(rows);
    setCurrent(rows.find((r) => r.status === 'active' || r.status === 'pending_payment') ?? rows[0] ?? null);
  }

  useEffect(() => {
    void load();
  }, [profile]);

  useEffect(() => {
    const payment = params.get('payment');
    if (payment === 'success') toast.success(t('sub.paymentSuccess'));
    if (payment === 'error') toast.error(t('sub.paymentError'));
  }, [params, t, toast]);

  async function startCheckout(plan: SubscriptionPlanKey) {
    if (!profile) return;
    setLoading(true);
    try {
      const data = await invokeGeniusPay({
        plan_key: plan,
        app_origin: window.location.origin,
      });
      if (!data.checkout_url) throw new Error('URL de paiement manquante');
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Paiement impossible');
      setLoading(false);
    }
  }

  async function payNextInstallment() {
    if (!profile || !current) return;
    setLoading(true);
    try {
      const data = await invokeGeniusPay({
        plan_key: current.plan_type,
        pay_next_installment: true,
        app_origin: window.location.origin,
      });
      if (!data.checkout_url) throw new Error('URL de paiement manquante');
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Paiement impossible');
      setLoading(false);
    }
  }

  const needsInstallment =
    current &&
    current.status === 'active' &&
    current.installments_paid < current.installment_count;

  const alert = getSubscriptionAlert(state, current);
  const installmentOverdue =
    current?.next_installment_at != null && (daysUntilDate(current.next_installment_at) ?? 1) < 0;

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">{t('sub.subtitle')}</p>
        </div>
        <article className="u-kpi" style={{ minHeight: 'auto', padding: '0.85rem 1.1rem' }}>
          <p className="u-kpi-label">{t('sub.current')}</p>
          <p className="u-kpi-value" style={{ fontSize: '1.15rem' }}>
            {current ? planLabel(current.plan_type, locale) : '—'}
          </p>
          <p className="u-kpi-meta">{current?.status ?? '—'}</p>
          {state.status === 'subscribed' && state.endsOn ? (
            <p className="u-kpi-meta">{t('sub.endsOn', { date: formatSubscriptionDate(state.endsOn, locale) })}</p>
          ) : null}
        </article>
      </div>

      {state.status === 'trial' ? (
        <div className={state.daysLeft <= 7 ? 'warning-banner' : 'success-banner'}>
          {t('sub.trialBanner', { days: state.daysLeft })}
        </div>
      ) : null}
      {state.status === 'locked' && state.reason === 'subscription_ended' ? (
        <div className="error-banner">{t('sub.subscriptionEndedBanner')}</div>
      ) : null}
      {state.status === 'locked' && state.reason === 'trial_ended' ? (
        <div className="error-banner">{t('sub.lockedBanner')}</div>
      ) : null}
      {alert && alert.kind !== 'trial_expiring' ? (
        <div
          className={
            alert.kind === 'installment_due' && alert.overdue ? 'error-banner' : 'warning-banner'
          }
        >
          {alert.kind === 'subscription_expiring'
            ? t('sub.alertSubscriptionExpiring', {
                days: alert.daysLeft,
                plan: planLabel(alert.planKey, locale),
              })
            : alert.overdue
              ? t('sub.alertInstallmentOverdue')
              : alert.daysLeft === 0
                ? t('sub.alertInstallmentDueToday')
                : t('sub.alertInstallmentDueSoon', { days: alert.daysLeft })}
        </div>
      ) : null}

      {needsInstallment ? (
        <section className={`u-card${installmentOverdue ? ' is-overdue' : ''}`} style={{ marginBottom: '1rem' }}>
          <p style={{ margin: 0 }}>
            {t('sub.installments', {
              paid: current.installments_paid,
              total: current.installment_count,
            })}
          </p>
          {current.next_installment_at ? (
            <p className={installmentOverdue ? 'error-banner' : 'muted'} style={installmentOverdue ? { marginTop: '0.75rem' } : undefined}>
              {t('sub.nextDue', { date: formatSubscriptionDate(current.next_installment_at, locale) })}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={() => void payNextInstallment()}
          >
            {t('sub.payNext')}
          </button>
        </section>
      ) : null}

      <div className="u-plan-grid">
        {SUBSCRIPTION_PLANS.map((p) => {
          const active = current?.plan_type === p.key && current.status === 'active';
          const label = locale === 'en' ? p.labelEn : p.label;
          const description = locale === 'en' ? p.descriptionEn : p.description;
          return (
            <section className={`u-plan-card${active ? ' is-active' : ''}`} key={p.key}>
              <h3>{label}</h3>
              <p className="u-plan-price">{formatEuro(p.price, locale)}</p>
              <p className="muted">{description}</p>
              <ul>
                {p.installmentAmounts.map((amt, i) => (
                  <li key={`${p.key}-${i}`}>
                    {locale === 'en' ? 'Installment' : 'Échéance'} {i + 1}: {formatEuro(amt, locale)}
                    {i > 0 ? (locale === 'en' ? ' (after 28 days)' : ' (après 28 j)') : ''}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading || active}
                onClick={() => void startCheckout(p.key)}
              >
                {active ? t('sub.currentPlan') : t('sub.pay')}
              </button>
            </section>
          );
        })}
      </div>

      {history.length > 0 ? (
        <section className="u-card">
          <h3 className="u-section-title" style={{ marginTop: 0 }}>
            {t('sub.history')}
          </h3>
          <ul className="u-list">
            {history.map((h) => (
              <li key={h.id} className="u-list-item">
                <div>
                  <strong>{planLabel(h.plan_type, locale)}</strong>
                  <div className="muted" style={{ fontSize: '0.88rem' }}>
                    {h.status}
                    {h.price != null ? ` · ${formatEuro(Number(h.price), locale)}` : ''}
                    {h.starts_on ? ` · ${formatSubscriptionDate(h.starts_on, locale)}` : ''}
                    {h.ends_on ? ` → ${formatSubscriptionDate(h.ends_on, locale)}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
